import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import "@/i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { initializeMobile, isNative } from "@/utils/mobile";
import { LocationProvider } from "@/contexts/LocationContext";
import { identifyUser, resetIdentity } from "@/lib/telemetrySDK";
import {
  getUserToken,
  setUserToken,
  setRefreshToken,
  clearAllUserState,
} from "@/lib/authStorage";

// Pages
import LandingPage from "@/pages/LandingPage";
import ExpertsPage from "@/pages/ExpertsPage";
import ExpertProfilePage from "@/pages/ExpertProfilePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import OAuthConsentPage from "@/pages/OAuthConsentPage";
import DashboardPage from "@/pages/DashboardPage";
import ExpertDashboardPage from "@/pages/ExpertDashboardPage";
import BookingSuccessPage from "@/pages/BookingSuccessPage";
import ReviewSuccessRedirect from "@/pages/ReviewSuccessRedirect";
import EnterprisePage from "@/pages/EnterprisePage";
import BecomeExpertPage from "@/pages/BecomeExpertPage";
import BuildersPage from "@/pages/BuildersPage";
import ReviewersPage from "@/pages/ReviewersPage";
import EmbedBadgePage from "@/pages/EmbedBadgePage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminLegalPage from "@/pages/AdminLegalPage";
import SourcingDashboardPage from "@/pages/SourcingDashboardPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import AdminForgotPasswordPage from "@/pages/AdminForgotPasswordPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import ExpertAgreementPage from "@/pages/ExpertAgreementPage";
import DPAPage from "@/pages/DPAPage";
import AcceptableUsePage from "@/pages/AcceptableUsePage";
import CookiePolicyPage from "@/pages/CookiePolicyPage";
import RecordingConsentPage from "@/pages/RecordingConsentPage";
import SubprocessorsPage from "@/pages/SubprocessorsPage";
import ReportContentPage from "@/pages/ReportContentPage";
import PrivacyRequestPage from "@/pages/PrivacyRequestPage";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ExpertEditProfilePage from "@/pages/ExpertEditProfilePage";
import ExpertOnboardingSuccessPage from "@/pages/ExpertOnboardingSuccessPage";
import ExpertPayoutSetupPage from "@/pages/ExpertPayoutSetupPage";
import AdminPayoutsPage from "@/pages/AdminPayoutsPage";
import VideoCallPage from "@/pages/VideoCallPage";
import ExpertBriefPage from "@/pages/ExpertBriefPage";
import VerdictSubmitPage from "@/pages/VerdictSubmitPage";
import VerificationDetailPage from "@/pages/VerificationDetailPage";
import SessionReviewPage from "@/pages/SessionReviewPage";
import BookingCheckoutPage from "@/pages/BookingCheckoutPage";
import ReviewCheckoutPage from "@/pages/ReviewCheckoutPage";
import APIKeysPage from "@/pages/APIKeysPage";
import GetVerifiedPage from "@/pages/GetVerifiedPage";
import PublicBadgePage from "@/pages/PublicBadgePage";
import ExpertReviewPage from "@/pages/ExpertReviewPage";
import UseCasesPage from "@/pages/UseCasesPage";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import MissionControlPage from "@/pages/MissionControlPage";

// Backend URL resolution.
//
// Dev (`npm start`): always use a relative path. setupProxy.js routes
//   /api/* server-side through the CRA dev server to whatever upstream
//   it's configured to hit (defaults to the prod backend) — bypasses
//   browser CORS entirely.
//
// Prod (`npm run build`): use the REACT_APP_BACKEND_URL env value baked
//   in at build time, or fall back to the current origin (Fly.io
//   single-domain deploy).
const IS_DEV = process.env.NODE_ENV === "development";
const BACKEND_URL = IS_DEV
  ? "" // same-origin → setupProxy.js intercepts
  : (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize mobile features
  useEffect(() => {
    initializeMobile();
  }, []);

  useEffect(() => {
    const token = getUserToken();
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Stitch any anonymous PostHog events from this session to the
      // now-known user. Idempotent + safe when PostHog isn't initialised
      // (REACT_APP_POSTHOG_KEY unset → no-op).
      identifyUser(response.data);
    } catch (error) {
      // Token rejected — clear EVERYTHING user-scoped (P0-3).
      clearAllUserState();
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  // Defence-in-depth: before issuing a new session, wipe any residual state
  // from a previous (possibly different) user on the same browser. Without
  // this, country/language/admin-token preferences leak across users (P0-3).
  // Also where we identify to PostHog — both login and register flow
  // through here, so one wiring point covers both auth paths.
  const _applyNewSession = (token, refreshToken, userData) => {
    clearAllUserState();
    setUserToken(token);
    if (refreshToken) setRefreshToken(refreshToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
    identifyUser(userData);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token, refresh_token, user: userData } = response.data;
    _applyNewSession(token, refresh_token, userData);
    return userData;
  };

  const register = async (email, password, name, company = null, userType = "user") => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      company,
      user_type: userType,
    });
    const { token, refresh_token, user: userData } = response.data;
    _applyNewSession(token, refresh_token, userData);
    return userData;
  };

  const logout = () => {
    // Clear EVERY user-scoped key (token, admin token, country, language)
    // so the next user on this browser starts clean (P0-3).
    clearAllUserState();
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    // Generate a fresh PostHog anonymous_id so the next session on this
    // browser isn't attributed to the user who just signed out.
    resetIdentity();
  };

  // BR-checkout-ack §5.1 — convenience accessors so components can branch on
  // `status` cleanly: 'loading' | 'authenticated' | 'unauthenticated'.
  // `refresh()` is an alias for fetchUser() so the public surface matches
  // the spec wording. Existing callers (user/loading/login/register/logout/
  // fetchUser) continue to work.
  const status = loading
    ? "loading"
    : user
      ? "authenticated"
      : "unauthenticated";

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      status,
      refresh: fetchUser,
      login,
      register,
      logout,
      fetchUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// App Router - Handles session_id detection for Google OAuth
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment synchronously during render for session_id
  // Using window.location.hash directly because useLocation().hash might not be updated on initial load
  // This prevents race conditions by processing Google OAuth callback FIRST
  const hash = window.location.hash;
  if (hash && (hash.includes('session_id=') || hash.includes('token='))) {
    console.log("AppRouter: Detected auth token in URL, rendering AuthCallback");
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/get-verified" element={<GetVerifiedPage />} />
      <Route path="/verified/:badgeSlug" element={<PublicBadgePage />} />
      <Route path="/embed/:badgeSlug" element={<EmbedBadgePage />} />
      <Route path="/use-cases" element={<UseCasesPage />} />
      <Route path="/experts" element={<ExpertsPage />} />
      <Route path="/experts/:id" element={<ExpertProfilePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/oauth/consent" element={<OAuthConsentPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/enterprise" element={<EnterprisePage />} />
      <Route path="/become-expert" element={<BecomeExpertPage />} />
      <Route path="/builders" element={<BuildersPage />} />
      <Route path="/reviewers" element={<ReviewersPage />} />
      {/* Live "thinking sessions" are not offered at launch (async only,
          founder decision 2026-10-09). Old links land on the written-review
          doorway instead of a dead-end booking page. */}
      <Route path="/brainstorm" element={<Navigate to="/get-verified" replace />} />
      <Route
        path="/expert-onboarding-success"
        element={
          <ProtectedRoute>
            <ExpertOnboardingSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expert/payouts/setup"
        element={
          <ProtectedRoute>
            <ExpertPayoutSetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expert-dashboard"
        element={
          <ProtectedRoute>
            <ExpertDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expert/edit-profile"
        element={
          <ProtectedRoute>
            <ExpertEditProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/session/:bookingId/review"
        element={
          <ProtectedRoute>
            <SessionReviewPage />
          </ProtectedRoute>
        }
      />
      <Route path="/booking/success" element={<BookingSuccessPage />} />
      {/* MCP Booking Checkout — public, no auth required */}
      <Route path="/booking/:bookingId/checkout" element={<BookingCheckoutPage />} />
      <Route path="/review/:reviewId/checkout" element={<ReviewCheckoutPage />} />
      {/* Stripe return URL for an async review (success_url in
          routes/bookings_mcp.py). Public: the buyer arrives from Stripe.
          Forwards to /verifications/:id, via login if signed out. */}
      <Route path="/review/:reviewId/success" element={<ReviewSuccessRedirect />} />
      <Route
        path="/api-keys"
        element={
          <ProtectedRoute>
            <APIKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expert/brief/:bookingId"
        element={
          <ProtectedRoute>
            <ExpertBriefPage />
          </ProtectedRoute>
        }
      />
      {/* BR-011 Tranche 1 — polymorphic brief for async reviews. The page
          reads reviewId from useParams and switches its fetch endpoint. */}
      <Route
        path="/expert/brief/review/:reviewId"
        element={
          <ProtectedRoute>
            <ExpertBriefPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expert/verdict/:bookingId"
        element={
          <ProtectedRoute>
            <VerdictSubmitPage />
          </ProtectedRoute>
        }
      />
      {/* BR-011 Tranche 1 — polymorphic verdict for async reviews. */}
      <Route
        path="/expert/verdict/review/:reviewId"
        element={
          <ProtectedRoute>
            <VerdictSubmitPage />
          </ProtectedRoute>
        }
      />
      {/* BR-011 Tranche 3.2 — buyer-side verification detail (verdict +
          attachments + rating prompt). */}
      <Route
        path="/verifications/:verificationId"
        element={
          <ProtectedRoute>
            <VerificationDetailPage />
          </ProtectedRoute>
        }
      />
      {/* New Xpert Verified async-review annotation surface */}
      <Route
        path="/expert/review/:verificationId"
        element={
          <ProtectedRoute>
            <ExpertReviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meeting/:bookingId"
        element={
          <ProtectedRoute>
            <VideoCallPage />
          </ProtectedRoute>
        }
      />
      
      {/* Legal & Info Pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
      <Route path="/legal/expert-agreement" element={<ExpertAgreementPage />} />
      <Route path="/legal/dpa" element={<DPAPage />} />
      <Route path="/legal/acceptable-use" element={<AcceptableUsePage />} />
      <Route path="/legal/cookies" element={<CookiePolicyPage />} />
      <Route path="/legal/recording-consent" element={<RecordingConsentPage />} />
      <Route path="/legal/subprocessors" element={<SubprocessorsPage />} />
      <Route path="/legal/report" element={<ReportContentPage />} />
      <Route path="/legal/privacy-request" element={<PrivacyRequestPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
      <Route path="/admin/legal" element={<AdminLegalPage />} />
      <Route path="/admin/sourcing" element={<SourcingDashboardPage />} />
      <Route path="/admin/payouts" element={<AdminPayoutsPage />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
      {/* QA-16: old links to /expert/dashboard (the route is /expert-dashboard). */}
      <Route path="/expert/dashboard" element={<Navigate to="/expert-dashboard" replace />} />

      {/* Mission Control — nested router, /* required */}
      <Route path="/mission-control/*" element={<MissionControlPage />} />

      {/* 404 catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  // Seed data removed — /seed endpoint now requires admin auth

  return (
    <AuthProvider>
      <LocationProvider>
        {/* App wrapper — light theme. The previous `dark` class was a
            leftover from the old aesthetic. v2 brand is paper-on-green;
            no dark mode in this build. */}
        <div className="App">
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#FFFFFF',                  /* card surface */
                border: '1px solid #DCDFD9',            /* slateline-200 (warm) */
                color: '#0E1410',                       /* ink */
                boxShadow: '0 8px 24px -12px rgba(14,20,16,0.08)',
              },
            }}
          />
          <BrowserRouter>
            <AppRouter />
            {/* GDPR + ePrivacy + CCPA cookie consent banner.
                Renders only on first visit (no localStorage record);
                opt-in, category-controlled, POSTs to /api/legal/acceptances
                if the user is signed in. */}
            <CookieConsentBanner />
          </BrowserRouter>
        </div>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;
